import React, { useEffect, useState } from "react";
import { Avatar, Button, Divider, TextField } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { BookingApi } from "../../API/Apis/BookingApi";
import { t } from "i18next";
import BookingDetalisListSideNavigation from "../../components/Layouts/ReservationsCard/BookingDetalisListSideNavigation";
import { createTheme, Theme } from "@mui/material/styles";
import { formatThemeForReservedArea } from "../../config/MuiColorThemes";
import CollapsibleNavbar from "../../components/Layouts/ReservedAreaDashboard/CollapseNavbar";
import BookingGetters from "../../store/onlineBooking/getters";

const USER_CACHE_KEY = "cachedUserProfile";

export default function ChangePassword() {
  const [dynamicTheme, setDynamicTheme] = useState<Theme | null>(null);
  const isSmallScreen = window.innerWidth <= 768;
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const appSettings = BookingGetters?.getAppSettings();
  const [showReserve, setShowReserve] = useState<boolean>(false);

  const cachedUser = (() => {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  })();
  const userInitials = `${cachedUser?.firstName?.[0] || ""}${
    cachedUser?.lastName?.[0] || ""
  }`.toUpperCase();

  const [toast, setToast] = useState({
    open: false,
    message: "",
    success: true,
  });

  const [changePassword, setChangePassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    async function fetchTheme() {
      const dynamicPalette = await formatThemeForReservedArea();
      setDynamicTheme(createTheme(dynamicPalette));
    }
    fetchTheme();
  }, []);

  useEffect(() => {
    setShowReserve(!!appSettings?.reservedArea);
  }, [appSettings]);

  const primaryColor = dynamicTheme?.palette?.primary?.main || "#2cb133";

  const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setChangePassword((prev) => ({ ...prev, oldPassword: e.target.value }));

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setChangePassword((prev) => ({ ...prev, newPassword: e.target.value }));

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) =>
    setChangePassword((prev) => ({ ...prev, confirmPassword: e.target.value }));

  const handleCancel = () => {
    setChangePassword({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  function saveNewPassword() {
    if (changePassword.newPassword === changePassword.confirmPassword) {
      BookingApi.changePassword(changePassword)
        .then((e) => {
          if (e.status === "success") {
            setToast({
              open: true,
              message: "Password Updated successfully",
              success: true,
            });
            handleCancel();
          }
          if (e.status === "error") {
            setToast({
              open: true,
              message: e.errorMessage,
              success: false,
            });
          }
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      setToast({
        open: true,
        message: "The confirmed password does not match the password.",
        success: false,
      });
    }
  }

  const avatarSrc = cachedUser?.profile_picture_base64
    ? cachedUser.profile_picture_base64.startsWith("data:image")
      ? cachedUser.profile_picture_base64
      : `data:image/png;base64,${cachedUser.profile_picture_base64}`
    : "";

  return (
    <>
      <div
        className="flex-row md:flex"
        style={{
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        {/* Sidebar — desktop only */}
        {showReserve && !isSmallScreen && !istabletScreen && (
          <div style={{ width: "260px", flexShrink: 0 }}>
            <BookingDetalisListSideNavigation />
          </div>
        )}

        {/* Collapsible navbar — mobile / tablet */}
        {showReserve && (isSmallScreen || istabletScreen) && (
          <CollapsibleNavbar callFrom="showReservations" />
        )}

        {/* Main content */}
        <div
          style={{
            flex: 1,
            padding: isSmallScreen ? "12px" : "",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* White card — fills the entire right area */}
          <div
            style={{
              backgroundColor: "#fff",
              padding: isSmallScreen ? "20px 16px" : "50px 48px",
              flex: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            {/* Page heading */}
            <p
              style={{
                fontSize: isSmallScreen ? "22px" : "26px",
                fontWeight: 700,
                color: "#222",
                margin: 0,
                lineHeight: 1.2,
                paddingLeft: isSmallScreen ? "" : "20px",
              }}
            >
              {t("Change Password")}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#9ca3af",
                margin: "6px 0 24px",
                paddingLeft: isSmallScreen ? "" : "20px",
              }}
            >
              {t("You can change your password from here.")}
            </p>

            {/* ── MOBILE layout: avatar on top, fields stacked below ── */}
            {isSmallScreen ? (
              <>
                {/* Avatar centered */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    marginBottom: "16px",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: "#9ca3af",
                      color: "#fff",
                      fontSize: "22px",
                      fontWeight: 700,
                      margin: "auto",
                    }}
                    src={avatarSrc}
                  >
                    {userInitials || "U"}
                  </Avatar>
                </div>

                {/* Fields — full width */}
                <TextField
                  margin="normal"
                  type="password"
                  required
                  fullWidth
                  label={t("Old Password")}
                  name="oldPassword"
                  id="oldPassword"
                  value={changePassword.oldPassword}
                  onChange={handleOldPasswordChange}
                  InputProps={{ style: { borderRadius: "8px" } }}
                />
                <TextField
                  margin="normal"
                  type="password"
                  required
                  fullWidth
                  label={t("New Password")}
                  name="newPassword"
                  id="newPassword"
                  value={changePassword.newPassword}
                  onChange={handleNewPasswordChange}
                  InputProps={{ style: { borderRadius: "8px" } }}
                />
                <TextField
                  margin="normal"
                  type="password"
                  required
                  fullWidth
                  label={t("Confirm Password")}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={changePassword.confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  InputProps={{ style: { borderRadius: "8px" } }}
                />

                <Divider style={{ margin: "24px 0" }} />

                {/* Buttons — full width on mobile */}
                <div style={{ display: "flex", gap: "12px" }}>
                  <Button
                    variant="contained"
                    onClick={saveNewPassword}
                    fullWidth
                    style={{
                      backgroundColor: primaryColor,
                      color: "#fff",
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      fontWeight: 600,
                      padding: "12px 0",
                      fontSize: "15px",
                    }}
                  >
                    {t("Save")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    fullWidth
                    style={{
                      borderColor: "#ccc",
                      color: "#333",
                      borderRadius: "8px",
                      textTransform: "capitalize",
                      fontWeight: 600,
                      padding: "12px 0",
                      fontSize: "15px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              </>
            ) : (
              /* ── DESKTOP layout: avatar left, fields right ── */
              <>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    alignItems: "flex-start",
                    width: "45%",
                    paddingLeft: "20px",
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: "#9ca3af",
                      color: "#fff",
                      fontSize: "18px",
                      fontWeight: 400,
                      flexShrink: 0,
                      mt: "60px",
                    }}
                    src={avatarSrc}
                  >
                    {userInitials || "U"}
                  </Avatar>

                  {/* Fields */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      padding: "40px",
                    }}
                  >
                    <TextField
                      margin="normal"
                      type="password"
                      required
                      fullWidth
                      label={t("Old Password")}
                      name="oldPassword"
                      id="oldPassword"
                      value={changePassword.oldPassword}
                      onChange={handleOldPasswordChange}
                      InputProps={{ style: { borderRadius: "8px" } }}
                    />
                    <TextField
                      margin="normal"
                      type="password"
                      required
                      fullWidth
                      label={t("New Password")}
                      name="newPassword"
                      id="newPassword"
                      value={changePassword.newPassword}
                      onChange={handleNewPasswordChange}
                      InputProps={{ style: { borderRadius: "8px" } }}
                    />
                    <TextField
                      margin="normal"
                      type="password"
                      required
                      fullWidth
                      label={t("Confirm Password")}
                      name="confirmPassword"
                      id="confirmPassword"
                      value={changePassword.confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      InputProps={{ style: { borderRadius: "8px" } }}
                    />
                    <Divider
                      style={{ margin: "40px 0px", maxWidth: "540px" }}
                    />

                    {/* Buttons */}
                    <div style={{ display: "flex", gap: "12px" }}>
                      <Button
                        variant="contained"
                        onClick={saveNewPassword}
                        style={{
                          backgroundColor: primaryColor,
                          color: "#fff",
                          borderRadius: "8px",
                          textTransform: "capitalize",
                          fontWeight: 600,
                          padding: "10px 32px",
                          fontSize: "15px",
                          width: "50%",
                        }}
                      >
                        {t("Save")}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        style={{
                          borderColor: "#ccc",
                          color: "#333",
                          borderRadius: "8px",
                          textTransform: "capitalize",
                          fontWeight: 600,
                          padding: "10px 32px",
                          fontSize: "15px",
                          backgroundColor: "#fff",
                          width: "50%",
                        }}
                      >
                        {t("Cancel")}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={toast.message}
        ContentProps={{
          sx: {
            backgroundColor: toast.success ? "#2e7d32" : "#d32f2f",
            color: "white",
            fontWeight: "bold",
            fontSize: "14px",
          },
        }}
      />
    </>
  );
}
